import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { Dispatch, ReactNode, SetStateAction, useCallback, useContext, useMemo } from 'react';
// import ArchiveSwitch from '@/modules/common/components/ArchiveSwitch.tsx';
import DocumentModal, {
  acceptFormat,
} from '@/modules/admin-dashboard/components/modals/DocumentModal.tsx';
import DocumentsFolderModal from '@/modules/admin-dashboard/components/modals/DocumentsFolderModal.tsx';
import { FiltersDocuments } from '@/modules/admin-dashboard/pages/Documents.tsx';
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FilterItem,
  ModalProviderContext,
  RadioButton,
  SearchField,
} from 'ui';
import { FilterLinesGrayIcon, FolderPlusIcon, UploadWhiteIcon } from 'ui/icons';
import { DocumentSortBy } from 'compliance-api';

const sortOptions = [
  {
    label: 'Creation Date (Oldest First)',
    value: DocumentSortBy.CreatedAtAsc,
  },
  {
    label: 'Creation Date (Newest First)',
    value: DocumentSortBy.CreatedAtDesc,
  },
  {
    label: 'Size (Smallest First)',
    value: DocumentSortBy.SizeAsc,
  },
  {
    label: 'Size (Largest First)',
    value: DocumentSortBy.SizeDesc,
  },
  {
    label: 'Format (A–Z)',
    value: DocumentSortBy.FormatAsc,
  },
  {
    label: 'Format (Z–A)',
    value: DocumentSortBy.FormatDesc,
  },
];

const formatOptions = [...acceptFormat, 'docx', 'xlsx', 'jpg', 'pptx'].map(format => ({
  value: format,
  label: format.toUpperCase(),
}));

interface DocumentsHeaderProps {
  filters: FiltersDocuments;
  setFilters: Dispatch<SetStateAction<FiltersDocuments>>;
  clientId?: number;
  hideClientSelector?: boolean;
  isPublic?: boolean;
}

const DocumentsHeader = ({
  filters,
  setFilters,
  clientId,
  hideClientSelector,
  isPublic = false,
}: DocumentsHeaderProps) => {
  const { from_date, to_date } = filters;
  const { openModal } = useContext(ModalProviderContext);

  const sort = filters.sort_by || DocumentSortBy.CreatedAtDesc;

  const handleChangeDateFilter = useCallback(
    (value: [Dayjs, Dayjs] | null) => {
      setFilters(filters =>
        value
          ? {
              ...filters,
              from_date: value[0].toISOString(),
              to_date: value[1].endOf('day').toISOString(),
            }
          : omit(filters, ['from_date', 'to_date']),
      );
    },
    [setFilters],
  );

  const handleChangeSearchFilter = useCallback(
    (filter_by_name: string) => {
      setFilters(filters =>
        filter_by_name ? { ...filters, filter_by_name } : omit(filters, ['filter_by_name']),
      );
    },
    [setFilters],
  );

  const handleChangeFormatFilter = useCallback(
    (filter_by_format: string | null) => {
      setFilters(filters =>
        filter_by_format ? { ...filters, filter_by_format } : omit(filters, ['filter_by_format']),
      );
    },
    [setFilters],
  );

  const handleSelectSort = useCallback(
    (sort_by: DocumentSortBy) => {
      setFilters(filters => ({ ...filters, sort_by }));
    },
    [setFilters],
  );

  const functionalSortOptions = useMemo<DropdownMenuItem<ReactNode>[]>(
    () =>
      sortOptions.map(({ label, value }) => ({
        label: <RadioButton items={[{ label, value }]} value={sort} onChange={() => 0} />,
        value,
        onClick: () => handleSelectSort(value),
      })),
    [handleSelectSort, sort],
  );

  const selectedSort = useMemo(() => sortOptions.find(({ value }) => value === sort), [sort]);

  // const handleToggleArchiveFilter = useCallback(
  //   (active: boolean) => {
  //     setFilters(filters =>
  //       active ? { ...filters, archived: true } : omit(filters, ['archived']),
  //     );
  //   },
  //   [setFilters],
  // );

  return (
    <div
      className={classNames('flex gap-3 flex-wrap justify-between border-b', {
        'px-3 sm:px-6 py-2.5': !isPublic,
        'pb-2.5': isPublic,
      })}
    >
      <div className="flex gap-2">
        <FilterItem
          type="date"
          value={
            from_date && to_date
              ? ([dayjs(from_date), dayjs(to_date)] as [Dayjs, Dayjs])
              : undefined
          }
          placeholder="Date added"
          showIcon={false}
          classes={{
            element: classNames({
              '[&_.min-w-40]:!min-w-[104px] [&_.min-w-56]:!min-w-[104px]': !isPublic,
              '[&_.min-w-40]:!min-w-56': isPublic,
            }),
          }}
          onChange={handleChangeDateFilter}
        />
        <FilterItem
          type="select"
          placeholder="Type"
          options={formatOptions}
          value={filters.filter_by_format}
          onChange={handleChangeFormatFilter}
          classes={{
            element:
              "w-[100px] [&_[class*='control']]:w-[85px]  [&_[class*='selectFilter']]:relative [&_[class*='selectFilter']]:top-[-2px]",
          }}
        />
      </div>
      <div className="flex flex-wrap gap-6">
        {/*<ArchiveSwitch active={!!filters.archived} onChange={handleToggleArchiveFilter} />*/}
        <DropdownMenu
          className="w-[250px]"
          containerClassName="w-fit"
          placement="bottom"
          button={
            <div
              className={classNames('flex gap-2 items-center text-sm w-fit', {
                'p-3 rounded-md h-10 border select-none hover:border-gray-900': isPublic,
              })}
            >
              <img src={FilterLinesGrayIcon} alt="sort" />
              <span className="text-gray-500">Sort:</span>
              <span>{selectedSort?.label || 'Not selected'}</span>
            </div>
          }
          items={functionalSortOptions}
        />
        <div className="max-h-9">
          <SearchField
            plain={!isPublic}
            allowClear
            iconOnPlain
            placeholder="Search"
            value={filters.filter_by_name || ''}
            onChange={handleChangeSearchFilter}
            className={classNames('w-fit ', {
              'max-w-24': !isPublic,
              // 'min-w-56': isPublic,
            })}
          />
        </div>

        {!isPublic && (
          <div className="flex gap-2.5">
            <Button
              onClick={() => openModal(DocumentsFolderModal, { clientId, hideClientSelector })}
              className="w-fit px-3.5 py-1.5 gap-2 min-h-9"
              variant="secondary"
            >
              <img src={FolderPlusIcon} alt="create folder" />
              <span>Create folder</span>
            </Button>
            <Button
              onClick={() => openModal(DocumentModal, { clientId, hideClientSelector })}
              className="w-fit px-3.5 py-1.5 gap-2 min-h-9"
            >
              <img src={UploadWhiteIcon} alt="upload file" />
              <span>Upload file</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsHeader;
